import React, { useState } from "react";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconSearch,
  IconFolders,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { useStoreSettings } from "../../controllers/settings.controller";
import {
  createAssetCategory,
  updateAssetCategory,
  deleteAssetCategory,
} from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetCategoriesTab({ categories, onRefresh }) {
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs.";

  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const [form, setForm] = useState({
    code: "",
    name: "",
    parent_id: "",
    description: "",
    classification: "Fixed Asset",
    useful_life_years: 5,
    depreciation_method: "Straight Line",
    residual_value_percent: 0,
    depreciation_start_convention: "Next Month",
    capitalization_threshold: 5000,
    asset_account: "Fixed Assets",
    accumulated_dep_account: "Accumulated Depreciation",
    dep_expense_account: "Depreciation Expense",
    is_active: 1,
  });

  const handleOpenAdd = () => {
    setEditingCategory(null);
    setForm({
      code: "",
      name: "",
      parent_id: "",
      description: "",
      classification: "Fixed Asset",
      useful_life_years: 5,
      depreciation_method: "Straight Line",
      residual_value_percent: 0,
      depreciation_start_convention: "Next Month",
      capitalization_threshold: 5000,
      asset_account: "Fixed Assets",
      accumulated_dep_account: "Accumulated Depreciation",
      dep_expense_account: "Depreciation Expense",
      is_active: 1,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (cat) => {
    setEditingCategory(cat);
    setForm({
      code: cat.code || "",
      name: cat.name || "",
      parent_id: cat.parent_id || "",
      description: cat.description || "",
      classification: cat.classification || "Fixed Asset",
      useful_life_years: cat.useful_life_years || 5,
      depreciation_method: cat.depreciation_method || "Straight Line",
      residual_value_percent: cat.residual_value_percent || 0,
      depreciation_start_convention: cat.depreciation_start_convention || "Next Month",
      capitalization_threshold: cat.capitalization_threshold || 5000,
      asset_account: cat.asset_account || "Fixed Assets",
      accumulated_dep_account: cat.accumulated_dep_account || "Accumulated Depreciation",
      dep_expense_account: cat.dep_expense_account || "Depreciation Expense",
      is_active: cat.is_active !== undefined ? cat.is_active : 1,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Category name is required");

    try {
      if (editingCategory) {
        await updateAssetCategory(editingCategory.id, form);
        toast.success("Category updated successfully!");
      } else {
        await createAssetCategory(form);
        toast.success("Category created successfully!");
      }
      setModalOpen(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Operation failed");
    }
  };

  const handleDelete = async (id, name, assetCount) => {
    if (assetCount > 0) {
      toast.error(`Cannot delete category "${name}" because it is referenced by ${assetCount} asset(s). Please deactivate it instead.`);
      return;
    }
    if (!confirm(`Are you sure you want to delete category "${name}"?`)) return;

    try {
      await deleteAssetCategory(id);
      toast.success("Category deleted successfully!");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to delete category");
    }
  };

  const handleToggleActive = async (cat) => {
    const nextState = cat.is_active ? 0 : 1;
    try {
      await updateAssetCategory(cat.id, { is_active: nextState });
      toast.success(`Category ${nextState ? "activated" : "deactivated"}!`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Failed to update category status");
    }
  };

  const filteredCategories = (categories || []).filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(q) ||
      (c.code || "").toLowerCase().includes(q) ||
      (c.description || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="bg-white dark:bg-neutral-800 p-4 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="relative w-full md:w-72">
          <input
            type="text"
            placeholder="Search category name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input input-sm input-bordered w-full pl-8"
          />
          <IconSearch size={16} className="absolute left-2.5 top-2.5 text-gray-400" stroke={iconStroke} />
        </div>

        <button
          onClick={handleOpenAdd}
          className="btn btn-sm bg-restro-green hover:bg-emerald-700 text-white border-0 flex items-center gap-1"
        >
          <IconPlus size={16} stroke={iconStroke} /> New Category
        </button>
      </div>

      {/* Categories Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-neutral-900/60 text-gray-600 dark:text-gray-300">
                <th>Code</th>
                <th>Category Name</th>
                <th>Parent Category</th>
                <th>Classification</th>
                <th>Default Useful Life</th>
                <th>Depr. Method</th>
                <th>Cap. Threshold</th>
                <th>Total Assets</th>
                <th className="text-right">Total Book Value</th>
                <th className="text-center">Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-10 text-gray-400">
                    <IconFolders size={36} className="mx-auto mb-2 opacity-30" stroke={iconStroke} />
                    No asset categories configured yet.
                  </td>
                </tr>
              ) : (
                filteredCategories.map((cat) => (
                  <tr key={cat.id} className="hover:bg-emerald-50/30 dark:hover:bg-neutral-700/30">
                    <td className="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                      {cat.code || "—"}
                    </td>
                    <td>
                      <div className="font-semibold text-gray-800 dark:text-white">{cat.name}</div>
                      {cat.description && <div className="text-xs text-gray-400">{cat.description}</div>}
                    </td>
                    <td className="text-xs text-gray-500">{cat.parent_name || "—"}</td>
                    <td>
                      <span className="badge badge-sm badge-outline">{cat.classification || "Fixed Asset"}</span>
                    </td>
                    <td>{cat.useful_life_years || 5} Years</td>
                    <td className="text-xs text-gray-600 dark:text-gray-300">{cat.depreciation_method || "Straight Line"}</td>
                    <td className="text-xs font-mono">
                      {currency} {Number(cat.capitalization_threshold || 0).toLocaleString()}
                    </td>
                    <td className="font-bold">{cat.total_assets || 0}</td>
                    <td className="text-right font-bold text-purple-600 dark:text-purple-400">
                      {currency} {Number(cat.total_book_value || 0).toLocaleString()}
                    </td>
                    <td className="text-center">
                      <button
                        onClick={() => handleToggleActive(cat)}
                        title={cat.is_active ? "Click to Deactivate" : "Click to Activate"}
                        className={`badge badge-sm cursor-pointer border-0 ${
                          cat.is_active ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {cat.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenEdit(cat)}
                          title="Edit"
                          className="btn btn-xs btn-ghost text-blue-600"
                        >
                          <IconEdit size={16} stroke={iconStroke} />
                        </button>
                        <button
                          onClick={() => handleDelete(cat.id, cat.name, cat.total_assets || 0)}
                          title="Delete"
                          className="btn btn-xs btn-ghost text-red-500"
                        >
                          <IconTrash size={16} stroke={iconStroke} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Category Modal (Create / Edit) */}
      {modalOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-4">
              {editingCategory ? "Edit Asset Category" : "Add Asset Category"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Category Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Kitchen Equipment, Vehicles, HVAC"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input input-sm input-bordered w-full mt-1"
                  />
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Category Code (Unique)</label>
                  <input
                    type="text"
                    placeholder="e.g. KITCHEN-EQ, VEH-01"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    className="input input-sm input-bordered w-full mt-1 font-mono uppercase"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Parent Category (Optional)</label>
                  <select
                    value={form.parent_id}
                    onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
                    className="select select-sm select-bordered w-full mt-1"
                  >
                    <option value="">None (Top Level Category)</option>
                    {(categories || [])
                      .filter((c) => !editingCategory || c.id !== editingCategory.id)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Asset Classification</label>
                  <select
                    value={form.classification}
                    onChange={(e) => setForm({ ...form, classification: e.target.value })}
                    className="select select-sm select-bordered w-full mt-1"
                  >
                    <option value="Fixed Asset">Fixed Asset (Capitalized)</option>
                    <option value="Low-Value Tracked Asset">Low-Value Tracked Asset</option>
                    <option value="Intangible Asset">Intangible Asset</option>
                    <option value="Leasehold Improvement">Leasehold Improvement</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Useful Life (Years)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={form.useful_life_years}
                    onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })}
                    className="input input-sm input-bordered w-full mt-1"
                  />
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Depreciation Method</label>
                  <select
                    value={form.depreciation_method}
                    onChange={(e) => setForm({ ...form, depreciation_method: e.target.value })}
                    className="select select-sm select-bordered w-full mt-1"
                  >
                    <option value="Straight Line">Straight Line</option>
                    <option value="Declining Balance">Declining Balance</option>
                    <option value="None">None</option>
                  </select>
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Residual Value %</label>
                  <input
                    type="number"
                    min="0"
                    max="99"
                    step="0.1"
                    value={form.residual_value_percent}
                    onChange={(e) => setForm({ ...form, residual_value_percent: e.target.value })}
                    className="input input-sm input-bordered w-full mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Capitalization Threshold ({currency})</label>
                  <input
                    type="number"
                    min="0"
                    value={form.capitalization_threshold}
                    onChange={(e) => setForm({ ...form, capitalization_threshold: e.target.value })}
                    className="input input-sm input-bordered w-full mt-1"
                  />
                </div>
                <div>
                  <label className="label-text font-semibold text-xs text-gray-500">Start Convention</label>
                  <select
                    value={form.depreciation_start_convention}
                    onChange={(e) => setForm({ ...form, depreciation_start_convention: e.target.value })}
                    className="select select-sm select-bordered w-full mt-1"
                  >
                    <option value="Next Month">Next Month</option>
                    <option value="Full Month">Full Month</option>
                    <option value="Daily">Daily</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label-text font-semibold text-xs text-gray-500">Description</label>
                <textarea
                  rows={2}
                  placeholder="Category guidelines and notes..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="textarea textarea-sm textarea-bordered w-full mt-1"
                />
              </div>

              <div className="modal-action justify-between mt-6">
                <button type="button" onClick={() => setModalOpen(false)} className="btn btn-sm btn-ghost">
                  Cancel
                </button>
                <button type="submit" className="btn btn-sm bg-restro-green hover:bg-emerald-700 text-white">
                  {editingCategory ? "Save Changes" : "Create Category"}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
    </div>
  );
}
